import { produce } from "immer";
import { escapeRegExp, sortBy } from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { doNothing, reactKeyFromIDSK } from "@examspark/common";
import {
  Button,
  Modal,
  ModalProps,
  Toolbar,
  useSelectedObjects,
  VFlex,
  HFlexBox,
  VSpace,
  SearchField,
  ObjectiveBadge,
  ObjectiveText,
  ToggleButtonGroup,
  PaneLayout,
} from "@examspark/common-ui";
import {
  IconInformationCircleLine,
  IconPageNextLine,
  IconPagePreviousLine,
  IconRejectLine,
} from "@examspark/common-ui/dist/components/icons";
import { Resource } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import ResourceViewer from "../../../resources/ResourceViewer";
import { useSearchPassages } from "../hooks/useSearchPassages";

import { Count } from "./ExamBuilder";
import { getObjectivesForItem } from "./NewActivityPassages";

const PassageInput = styled.div`
  button {
    input {
      margin: 0;
    }
  }

  button:hover {
    background-color: #ddd;
  }
`;
const PassageMeta = styled(VSpace)`
  gap: 0.5rem;
  color: #666;
  flex: 1;
`;
const PassageCount = styled.div`
  width: 4rem;
  margin-left: 1rem;
`;
const PassageObjectives = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const NavigationToolbar = styled.div`
  padding: 1rem;
  background-color: #f7f7f7;
  border-bottom: solid 1px #ddd;
`;

const TableHeader = styled.div`
  padding: 1rem;
  display: flex;
  align-items: flex-end;
  border-bottom: solid 1px #ddd;
  color: #777;
  line-height: 1.3em;
`;
const TableHeaderPassage = styled.div`
  flex: 1;
`;
const TableHeaderQty = styled.div`
  text-align: right;
`;

interface PassageProps {
  selected?: boolean;
}
const Passage = styled.div<PassageProps>`
  display: flex;
  border-radius: 0.3rem;
  padding: 1rem;
  cursor: pointer;
  align-items: center;
  border-top: solid 1px #ddd;
  transition: background-color 0.2s;
  gap: 1rem;

  ${(props) => {
    if (props.selected) {
      return `
        background-color: #f2f2f2;
        ${PassageMeta} {
          color: #222;
        }
      `;
    }
  }}

  &:first-child {
    border-top: none;
  }

  &:hover {
    background-color: #f7f7f7;
  }
`;

const PassageName = styled.span`
  font-weight: 500;
  color: #222;
`;
// const PassageItemCount = styled.span`
//   color: #777;
// `;

const MainPane = styled(PaneLayout.Pane)`
  overflow-y: auto;
  flex: 1;
  min-height: 175px;
`;
const PreviewPane = styled(PaneLayout.Pane)`
  border-left: solid 2px #ccc;
  flex: 2;
  min-height: 0;
  max-width: 50rem;
`;
const PreviewMain = styled.div`
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
`;
const PreviewPaneObjectives = styled.div`
  position: relative;
  border: solid 1px #ddd;
  padding: 1rem;
  padding-right: 5rem;
  margin-bottom: 1rem;
  background-color: #ffffec;
`;
const PreviewPaneToolbarObjective = styled.div`
  margin-top: 0.5rem;
  &:first-child {
    margin-top: 0;
  }
`;
const PreviewToolbar = styled(HFlexBox)`
  padding: 1rem;
  background-color: #f7f7f7;
  border-bottom: solid 1px #ddd;
  gap: 2rem;
`;
const PreviewToolbarClose = styled(HFlexBox)`
  gap: 0.5rem;
`;
const PreviewToolbarNav = styled(HFlexBox)`
  gap: 0.5rem;
  flex: 1;
`;

const PaneLayoutPassageList = styled(PaneLayout.Pane).attrs({
  scroll: "y",
})`
  flex: 1;
  min-width: 15rem;
`;

const PaneLayoutPassageListInner = styled.div`
  min-width: 30rem;
`;

const SearchPane = styled(HFlexBox)`
  padding: 1rem;
  border-bottom: solid 1px #ddd;
  background-color: #f2f2f2;
  align-items: center;
`;

const SearchPaneFields = styled(HFlexBox)`
  gap: 0.5rem;
  flex: 1;
`;

const SearchPaneFieldsSearch = styled.div`
  flex: 1;
`;
const SearchPaneFieldsSelect = styled.div`
  flex: 1;
  max-width: 15rem;

  select {
    width: 100%;
  }
`;

const PassagePreviewWrapper = styled.div`
  /* max-width: 50rem; */
  /* margin: 0 auto; */
`;

const InfoBanner = styled(HFlexBox)`
  gap: 1rem;
  padding: 1rem 1rem;
  color: #6b6b6b;
  background-color: #ffffec;
  border-bottom: solid 1px #ddd;
  align-items: center;
  line-height: 1.5em;

  svg {
    fill: #333;
  }
`;

const PassageSelectModal = (
  props: ModalProps & {
    objectiveAuthorityId?: string;
    onSubmit?: (passages: Resource[]) => void;
  },
) => {
  const [state] = useContext(NewActivityContext);
  const selected = useSelectedObjects<Resource>([]);
  const [preview, setPreview] = useState<Resource>();
  const [filterTitle, setFilterTitle] = useState("");
  const [filterGenre, setFilterGenre] = useState<string>();
  const [filterObjective, setFilterObjective] = useState<string>();
  const { results, facets, fetchPassages, genreFacets, objectiveFacets } =
    useSearchPassages();
  const [previewMode, setPreviewMode] = useState<
    "content" | "standards" | "items"
  >("content");
  // const [facets, setfacets] = useState<{ key: string; count: number }[]>();
  // const [results, setResults] = useState<Resource[]>();

  const objectiveIds = state.objectives.map((obj) => obj.id).join(",");
  useEffect(() => {
    fetchPassages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveIds, state.language]);

  useEffect(() => {
    if (preview?.id) {
      document.getElementById(preview.id)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [preview]);

  const filteredResults = useMemo(() => {
    const next = results
      ?.filter((item) => {
        if (
          filterTitle.length &&
          item.name &&
          !item.name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            ?.match(new RegExp(escapeRegExp(filterTitle), "i"))
        ) {
          return false;
        }

        const objectives = getObjectivesForItem(item);

        if (
          !!filterObjective?.length &&
          !objectives.find((obj) => obj.id === filterObjective)
        ) {
          return false;
        }

        if (
          !!filterGenre &&
          !item.tags?.find((tag) => {
            if (!tag) return false;
            return tag.key === "genre" && tag?.value === filterGenre;
          })
        ) {
          return false;
        }

        return true;
      })
      .map((result) => {
        return produce(result, (draft) => {
          const objectives = getObjectivesForItem(result);
          const matchingObjectiveCount = state.objectives.filter((obj) => {
            return objectives.find((alignment) => alignment.id === obj.id);
          }).length;

          // console.log("result", result);
          /** @ts-expect-error sequence number/string mismatch */
          draft.sequence = matchingObjectiveCount ?? 0;
          // console.log("count", matchingObjectiveCount);
          // return result;
        });
      });

    return sortBy(next, ["sequence"]).reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterGenre, filterObjective, filterTitle, results]);

  return (
    <Modal
      size="full"
      title="Select Passages"
      flexBodyColumn
      onAfterClose={() => {
        selected.clear();
        setPreview(undefined);
      }}
      buttons={
        <>
          <Button
            title="Close"
            data-cy="modal-button-close"
            variant="contained"
            onClick={props.onRequestClose}
            // disabled={props.isLoading}
          >
            Close
          </Button>
          <Button
            title="Select Passages"
            data-cy="modal-button-submit"
            autoFocus
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!selected.values) return;
              props.onSubmit?.(selected.values);
            }}
            // isLoading={props.isLoading}
            disabled={!selected.values.length}
          >
            Select {selected.values.length} Passage
            {selected.values.length > 1 && "s"}
          </Button>
        </>
      }
      {...props}
    >
      <PaneLayout direction="vertical">
        <InfoBanner>
          <IconInformationCircleLine width="16" />
          <div>
            Passages are sorted in order of relevance to the standards you
            selected to assess. These standards are highlighted below.
          </div>
        </InfoBanner>
        <SearchPane>
          <SearchPaneFields>
            <SearchPaneFieldsSearch>
              <SearchField
                placeholder="Search Title"
                value={filterTitle}
                onChange={(value) => setFilterTitle(value)}
              />
            </SearchPaneFieldsSearch>
            <SearchPaneFieldsSelect>
              <select
                value={filterObjective}
                onChange={(e) => {
                  setFilterObjective(e.target.value);
                }}
              >
                <option value="">Filter Standard</option>
                <optgroup label="Selected Standards">
                  {sortBy(objectiveFacets, ["sequence"])?.map((obj) => {
                    if (!state.objectives.find((o) => o.id === obj.value)) {
                      return null;
                    }
                    return (
                      <option key={obj.value} value={obj.value}>
                        {obj.label} ({obj.count}{" "}
                        {obj.count === 1 ? "Item" : "Items"})
                      </option>
                    );
                  })}
                </optgroup>
                <optgroup label="Other Standards">
                  {sortBy(objectiveFacets, ["sequence"])?.map((obj) => {
                    return (
                      <option key={obj.value} value={obj.value}>
                        {obj.label} ({obj.count}{" "}
                        {obj.count === 1 ? "Item" : "Items"})
                      </option>
                    );
                  })}
                </optgroup>
              </select>
            </SearchPaneFieldsSelect>
            <SearchPaneFieldsSelect>
              <select
                value={filterGenre}
                onChange={(e) => {
                  setFilterGenre(e.target.value);
                }}
              >
                <option value="">Filter Genre</option>
                {sortBy(genreFacets, ["label"])?.map((obj) => {
                  return (
                    <option key={obj.value} value={obj.value}>
                      {obj.label} ({obj.count}{" "}
                      {obj.count === 1 ? "Passage" : "Passages"})
                    </option>
                  );
                })}
              </select>
            </SearchPaneFieldsSelect>
          </SearchPaneFields>
        </SearchPane>
        <MainPane>
          <PaneLayout direction="horizontal">
            <PaneLayoutPassageList>
              <VFlex>
                <NavigationToolbar>
                  <Toolbar>
                    <Button
                      title=""
                      variant="outlined"
                      disabled={
                        filteredResults &&
                        filteredResults?.findIndex(
                          (psg) => psg.id === preview?.id,
                        ) === 0
                      }
                      onClick={() => {
                        if (!filteredResults) return;
                        const previewIndex = filteredResults?.findIndex(
                          (psg) => psg.id === preview?.id,
                        );
                        if (previewIndex === 0) return;
                        setPreview(filteredResults[previewIndex - 1]);
                      }}
                      icon={<IconPagePreviousLine />}
                    />
                    <Button
                      title=""
                      variant="outlined"
                      disabled={
                        filteredResults &&
                        filteredResults?.findIndex(
                          (psg) => psg.id === preview?.id,
                        ) ===
                          filteredResults.length - 1
                      }
                      onClick={() => {
                        if (!filteredResults) return;
                        const previewIndex = filteredResults?.findIndex(
                          (psg) => psg.id === preview?.id,
                        );
                        if (previewIndex === (filteredResults?.length || 0) - 1)
                          return;
                        setPreview(filteredResults[previewIndex + 1]);
                      }}
                      icon={<IconPageNextLine />}
                    />
                    <Button
                      title=""
                      variant="outlined"
                      disabled={!preview}
                      onClick={(e) => {
                        e.stopPropagation();
                        selected.toggle(preview);
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!preview}
                        checked={preview && selected.contains(preview)}
                      />
                    </Button>
                  </Toolbar>
                </NavigationToolbar>
                <TableHeader>
                  <TableHeaderPassage>Passage Info</TableHeaderPassage>
                  <TableHeaderQty>
                    Item Quantity <br />
                    Matching Standards
                  </TableHeaderQty>
                </TableHeader>
                <VFlex.Main>
                  <PaneLayoutPassageListInner>
                    {filteredResults?.map((item, idx) => {
                      const find = facets?.find((f) => f.key === item.id);
                      const count = find?.doc_count || "";
                      const objectives = getObjectivesForItem(item);
                      return (
                        <Passage
                          key={item.id}
                          id={item.id}
                          onClick={() => {
                            setPreview(item);
                          }}
                          selected={preview?.id === item.id}
                        >
                          <PassageInput>
                            <Button
                              title=""
                              variant="plain"
                              onClick={(e) => {
                                e.stopPropagation();
                                selected.toggle(item);
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selected.contains(item) ||
                                  !!state.passages.find(
                                    (psg) => psg.id === item.id,
                                  )
                                }
                                onChange={doNothing}
                                disabled={
                                  !!state.passages.find(
                                    (psg) => psg.id === item.id,
                                  )
                                }
                              />
                            </Button>
                          </PassageInput>
                          <PassageMeta>
                            <div>
                              <PassageName>{item.name}</PassageName>{" "}
                            </div>
                            <PassageObjectives>
                              {sortBy(objectives, ["sequence"])
                                .filter((obj) => {
                                  const authority = obj.scope?.split("#")[0];
                                  if (
                                    props.objectiveAuthorityId &&
                                    authority !== props.objectiveAuthorityId
                                  ) {
                                    return false;
                                  }
                                  return true;
                                })
                                .map((obj) => (
                                  <ObjectiveBadge
                                    key={obj.id}
                                    // title={obj.content || ""}
                                    // highlight={
                                    //   state.objectives.find(
                                    //     (_obj) => _obj.id === obj.id
                                    //   )
                                    //     ? true
                                    //     : false
                                    // }
                                  >
                                    {obj.abbreviation}
                                  </ObjectiveBadge>
                                ))}
                            </PassageObjectives>
                          </PassageMeta>
                          <PassageCount>
                            <Count>{count}</Count>
                          </PassageCount>
                        </Passage>
                      );
                    })}
                  </PaneLayoutPassageListInner>
                </VFlex.Main>
              </VFlex>
            </PaneLayoutPassageList>
            {preview && (
              <PreviewPane>
                <PaneLayout direction="vertical">
                  <PreviewToolbar>
                    <PreviewToolbarNav>
                      <ToggleButtonGroup
                        color="light-gray"
                        value={previewMode}
                        onClick={(value) =>
                          setPreviewMode(value as typeof previewMode)
                        }
                      >
                        <ToggleButtonGroup.Button value="content">
                          Content
                        </ToggleButtonGroup.Button>
                        <ToggleButtonGroup.Button value="standards">
                          Standards
                        </ToggleButtonGroup.Button>
                        {/* <ToggleButtonGroup.Button value="items">
                          Items
                        </ToggleButtonGroup.Button> */}
                      </ToggleButtonGroup>
                    </PreviewToolbarNav>
                    <PreviewToolbarClose>
                      <Button
                        title=""
                        variant="contained"
                        onClick={() => setPreview(undefined)}
                        icon={<IconRejectLine />}
                      >
                        Close Preview
                      </Button>
                    </PreviewToolbarClose>
                  </PreviewToolbar>
                  <PreviewMain>
                    {previewMode === "standards" && (
                      <PreviewPaneObjectives>
                        <div>
                          {sortBy(getObjectivesForItem(preview), ["sequence"])
                            .filter((obj) => {
                              const authority = obj.scope?.split("#")[0];
                              if (
                                props.objectiveAuthorityId &&
                                authority !== props.objectiveAuthorityId
                              ) {
                                return false;
                              }
                              return true;
                            })
                            .map((obj) => {
                              return (
                                <PreviewPaneToolbarObjective
                                  key={reactKeyFromIDSK(obj)}
                                >
                                  <ObjectiveText objective={obj} />
                                </PreviewPaneToolbarObjective>
                              );
                            })}
                        </div>
                      </PreviewPaneObjectives>
                    )}
                    {previewMode === "content" && (
                      <PassagePreviewWrapper>
                        <ResourceViewer
                          resourceKey={{
                            id: preview.id,
                            sk: preview.sk,
                          }}
                        />
                      </PassagePreviewWrapper>
                    )}
                  </PreviewMain>
                </PaneLayout>
              </PreviewPane>
            )}
          </PaneLayout>
        </MainPane>
      </PaneLayout>
    </Modal>
  );
};

export default PassageSelectModal;
